/**
 * Indicator control panel – view and edit all indicators, recommendations,
 * detailed questions, enabling flag, and examples in one screen.
 * Includes visual tree view and tree logic (gaps, orphans, numbering).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { colors, spacing, fonts, radii, buttonBase, shadows } from '../styles/theme';
import { Phase, PHASE_LABELS } from '../types';
import {
    fetchAdminIndicators,
    saveAdminIndicators,
    trashIndicator,
    restoreFromTrash,
    createIndicator,
    ConflictError,
    AdminIndicatorRow,
    AdminExampleRow,
    AdminPhaseData,
} from '../services/api';
import {
    buildIndicatorTree,
    detectNumberingGaps,
    detectOrphans,
    detectDuplicates,
    suggestNextId,
    getDescendantIds,
    getChildIds,
    getGapsForParent,
    getParentId,
    TreeIndicatorNode,
} from '../utils/indicatorTreeUtils';

const ADMIN_USER = (process.env.REACT_APP_ADMIN_USER || '').trim();
const ADMIN_PASS = (process.env.REACT_APP_ADMIN_PASS || '').trim();

const PHASES: Phase[] = ['phase1', 'phase2', 'phase3', 'phase4'];
type PhaseOrTrash = Phase | 'trash';

interface IndicatorWithExamples extends AdminIndicatorRow {
    examples: AdminExampleRow[];
}

function buildIndicatorsWithExamples(phaseData: AdminPhaseData): IndicatorWithExamples[] {
    const byId = new Map<string, AdminExampleRow[]>();
    for (const ex of phaseData.examples) {
        const list = byId.get(ex.indicatorId) || [];
        list.push(ex);
        byId.set(ex.indicatorId, list);
    }
    return phaseData.indicators.map((ind) => ({
        ...ind,
        examples: byId.get(ind.id) || [],
    }));
}

interface IndicatorVisualizerProps {
    onBack?: () => void;
}

const IndicatorVisualizer: React.FC<IndicatorVisualizerProps> = ({ onBack }) => {
    const [phasesData, setPhasesData] = useState<Record<string, AdminPhaseData> | null>(null);
    const [phaseVersions, setPhaseVersions] = useState<Record<string, number>>({});
    const [selectedPhase, setSelectedPhase] = useState<PhaseOrTrash>('phase1');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saveConflict, setSaveConflict] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [viewMode, setViewMode] = useState<'flat' | 'tree'>('tree');
    const [restoreConflict, setRestoreConflict] = useState<{ indicatorId: string; originalPhase: Phase } | null>(null);
    const [restoreNewId, setRestoreNewId] = useState('');
    const [trashConfirm, setTrashConfirm] = useState<{ phase: Phase; indicatorId: string; childCount: number } | null>(null);
    const [restructure, setRestructure] = useState<{ phase: Phase; indicatorId: string; currentId: string } | null>(null);
    const [restructureNewId, setRestructureNewId] = useState('');
    const [treeEditModal, setTreeEditModal] = useState<{ phase: Phase; indicatorId: string } | null>(null);
    const [isAdminAuthed, setIsAdminAuthed] = useState(false);
    const [adminUser, setAdminUser] = useState('');
    const [adminPass, setAdminPass] = useState('');
    const [authError, setAuthError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        setSaveConflict(false);
        try {
            const res = await fetchAdminIndicators();
            setPhasesData(res.phases);
            setPhaseVersions(res.versions ?? {});
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        try {
            if (typeof window !== 'undefined') {
                const saved = window.localStorage.getItem('indicatorAdminAuth');
                if (saved === 'ok') {
                    setIsAdminAuthed(true);
                }
            }
        } catch {
            // ignore storage errors
        }
    }, []);

    const handleAuthSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (adminUser.trim() === ADMIN_USER && adminPass.trim() === ADMIN_PASS) {
            setIsAdminAuthed(true);
            setAuthError(null);
            try {
                if (typeof window !== 'undefined') {
                    window.localStorage.setItem('indicatorAdminAuth', 'ok');
                }
            } catch {
                // ignore storage errors
            }
        } else {
            setAuthError('Incorrect username or password');
        }
    };

    const updateIndicator = useCallback(
        (phase: string, index: number, field: keyof AdminIndicatorRow, value: string | boolean) => {
            if (!phasesData || phase === 'trash') return;
            const data = phasesData[phase];
            const next = [...data.indicators];
            (next[index] as any)[field] = value;
            setPhasesData({ ...phasesData, [phase]: { ...data, indicators: next } });
        },
        [phasesData]
    );

    const updateExample = useCallback(
        (phase: string, indicatorId: string, exampleIndex: number, field: 'exampleArea' | 'example', value: string) => {
            if (!phasesData || phase === 'trash') return;
            const data = phasesData[phase];
            const examplesForId = data.examples.filter((e) => e.indicatorId === indicatorId);
            const updated = [...examplesForId];
            if (!updated[exampleIndex]) return;
            updated[exampleIndex] = { ...updated[exampleIndex], [field]: value };
            const other = data.examples.filter((e) => e.indicatorId !== indicatorId);
            const newExamples = [...other, ...updated];
            setPhasesData({ ...phasesData, [phase]: { ...data, examples: newExamples } });
        },
        [phasesData]
    );

    const addExample = useCallback(
        (phase: string, indicatorId: string) => {
            if (!phasesData || phase === 'trash') return;
            const data = phasesData[phase];
            const newExamples = [...data.examples, { indicatorId, exampleArea: '', example: '' }];
            setPhasesData({ ...phasesData, [phase]: { ...data, examples: newExamples } });
        },
        [phasesData]
    );

    const removeExample = useCallback(
        (phase: string, indicatorId: string, exampleIndex: number) => {
            if (!phasesData || phase === 'trash') return;
            const data = phasesData[phase];
            const examplesForId = data.examples.filter((e) => e.indicatorId === indicatorId);
            const removed = examplesForId[exampleIndex];
            const newExamples = data.examples.filter((e) => !(e.indicatorId === indicatorId && e === removed));
            setPhasesData({ ...phasesData, [phase]: { ...data, examples: newExamples } });
        },
        [phasesData]
    );

    const handleSave = useCallback(async () => {
        if (!phasesData || selectedPhase === 'trash') return;
        const phase = selectedPhase as Phase;
        setSaving(true);
        setSaveSuccess(false);
        setSaveConflict(false);
        setError(null);
        try {
            const data = phasesData[phase];
            const result = await saveAdminIndicators(phase, {
                indicators: data.indicators,
                examples: data.examples.map((e) => ({
                    indicator_id: e.indicatorId,
                    example_area: e.exampleArea,
                    example: e.example,
                })),
                version: phaseVersions[phase],
            });
            // Update our local version so a second save works without reloading.
            if (result.version !== undefined) {
                setPhaseVersions((prev) => ({ ...prev, [phase]: result.version }));
            }
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (e) {
            if (e instanceof ConflictError) {
                setSaveConflict(true);
            } else {
                setError(e instanceof Error ? e.message : 'Failed to save');
            }
        } finally {
            setSaving(false);
        }
    }, [phasesData, selectedPhase, phaseVersions]);

    const handleTrash = useCallback(
        async (phase: Phase, indicatorId: string, withChildren?: boolean) => {
            if (!phasesData) return;
            const indicators = phasesData[phase].indicators;
            const descendantIds = getDescendantIds(indicators, indicatorId);
            if (descendantIds.length > 0 && !withChildren) {
                setTrashConfirm({ phase, indicatorId, childCount: descendantIds.length });
                return;
            }
            setTrashConfirm(null);
            setSaving(true);
            setError(null);
            try {
                // Trash descendants first (deepest first: sort by number of dots desc)
                const toTrash = withChildren ? [...descendantIds.sort((a, b) => b.split('.').length - a.split('.').length), indicatorId] : [indicatorId];
                for (const id of toTrash) {
                    await trashIndicator(phase, id);
                }
                await load();
                setSelectedPhase('trash');
            } catch (e) {
                setError(e instanceof Error ? e.message : 'Failed to trash');
            } finally {
                setSaving(false);
            }
        },
        [phasesData, load]
    );

    const handleRestore = useCallback(
        async (indicatorId: string, restoreAsId?: string, originalPhase?: string) => {
            setSaving(true);
            setError(null);
            try {
                await restoreFromTrash(indicatorId, restoreAsId, originalPhase);
                setRestoreConflict(null);
                setRestoreNewId('');
                await load();
            } catch (e) {
                setError(e instanceof Error ? e.message : 'Failed to restore');
            } finally {
                setSaving(false);
            }
        },
        [load]
    );

    /** Open restore flow: if id exists in target phase, show conflict modal; else restore directly. */
    const startRestore = useCallback(
        (ind: { id: string; original_phase?: string }) => {
            const phase = (ind.original_phase || 'phase1') as Phase;
            const existing = phasesData?.[phase]?.indicators ?? [];
            const idExists = existing.some((i) => i.id === ind.id);
            if (idExists) {
                setRestoreConflict({ indicatorId: ind.id, originalPhase: phase });
                setRestoreNewId(suggestNextId(existing, null));
            } else {
                handleRestore(ind.id, undefined, phase);
            }
        },
        [phasesData, handleRestore]
    );

    const handleCreate = useCallback(
        async (data: { id: string; question: string; recommendation: string; detailedQuestion: string; enabling?: boolean; examples?: Array<{ indicator_id: string; example_area: string; example: string }> }) => {
            if (selectedPhase === 'trash') return;
            setSaving(true);
            setError(null);
            try {
                await createIndicator(selectedPhase, data);
                setCreateModalOpen(false);
                await load();
            } catch (e) {
                setError(e instanceof Error ? e.message : 'Failed to create');
            } finally {
                setSaving(false);
            }
        },
        [selectedPhase, load]
    );

    const applyRestructure = useCallback(
        (phase: Phase, oldId: string, newId: string) => {
            if (!phasesData || !newId.trim() || newId.trim() === oldId) return;
            const tid = newId.trim();
            const data = phasesData[phase];
            if (data.indicators.some((i) => i.id === tid)) return;
            const nextIndicators = data.indicators.map((i) => (i.id === oldId ? { ...i, id: tid } : i));
            const nextExamples = data.examples.map((e) =>
                e.indicatorId === oldId ? { ...e, indicatorId: tid } : e
            );
            setPhasesData({ ...phasesData, [phase]: { ...data, indicators: nextIndicators, examples: nextExamples } });
            setRestructure(null);
            setRestructureNewId('');
        },
        [phasesData]
    );

    const phaseData = phasesData?.[selectedPhase];
    const indicatorsWithExamples = phaseData ? buildIndicatorsWithExamples(phaseData) : [];
    const indicators = phaseData?.indicators ?? [];

    const treeIssues = useMemo(() => {
        if (selectedPhase === 'trash' || !indicators.length) return { gaps: [], orphans: [], duplicates: [] };
        return {
            gaps: detectNumberingGaps(indicators),
            orphans: detectOrphans(indicators),
            duplicates: detectDuplicates(indicators),
        };
    }, [selectedPhase, indicators]);

    const indicatorTree = useMemo(() => {
        if (selectedPhase === 'trash' || !indicators.length) return [];
        return buildIndicatorTree(indicators);
    }, [selectedPhase, indicators]);

    const hasTreeIssues = treeIssues.gaps.length > 0 || treeIssues.orphans.length > 0 || treeIssues.duplicates.length > 0;

    if (!isAdminAuthed) {
        return (
            <div style={styles.loading}>
                <div style={styles.adminAuthBox}>
                    <h1 style={styles.title}>Indicator control panel</h1>
                    <p style={{ ...styles.subtitle, color: colors.no, fontWeight: 600 }}>
                        For Emmie and Laurynas only.
                    </p>
                    <form onSubmit={handleAuthSubmit} style={{ display: 'flex', flexDirection: 'column', gap: spacing[3], marginTop: spacing[4], width: '100%' }}>
                        <label style={styles.fieldLabel}>Username</label>
                        <input
                            style={styles.input}
                            value={adminUser}
                            onChange={(e) => setAdminUser(e.target.value)}
                            autoComplete="username"
                        />
                        <label style={styles.fieldLabel}>Password</label>
                        <input
                            style={styles.input}
                            type="password"
                            value={adminPass}
                            onChange={(e) => setAdminPass(e.target.value)}
                            autoComplete="current-password"
                        />
                        {authError && <span style={styles.error}>{authError}</span>}
                        <div style={styles.modalActions}>
                            {onBack && (
                                <button type="button" style={styles.cancelButton} onClick={onBack}>
                                    ← Back
                                </button>
                            )}
                            <button type="submit" style={styles.saveButton}>
                                Enter
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={styles.loading}>
                <p>Loading indicators…</p>
            </div>
        );
    }

    if (error && !phasesData) {
        return (
            <div style={styles.loading}>
                <p style={{ color: colors.no }}>{error}</p>
                <button style={styles.button} onClick={load}>
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <header style={styles.header}>
                <div style={styles.headerRow}>
                    {onBack && (
                        <button type="button" style={styles.backButton} onClick={onBack}>
                            ← Back
                        </button>
                    )}
                    <div>
                        <h1 style={styles.title}>Indicator control panel</h1>
                        <p style={styles.subtitle}>
                            View and edit indicators, recommendations, detailed questions, enabling flag, and examples. Save per phase.
                        </p>
                    </div>
                </div>
            </header>

            <div style={styles.tabs}>
                {PHASES.map((p) => (
                    <button
                        key={p}
                        type="button"
                        style={{
                            ...styles.tab,
                            ...(selectedPhase === p ? styles.tabActive : {}),
                        }}
                        onClick={() => setSelectedPhase(p)}
                    >
                        {PHASE_LABELS[p]}
                    </button>
                ))}
                <button
                    type="button"
                    style={{
                        ...styles.tab,
                        ...styles.tabTrash,
                        ...(selectedPhase === 'trash' ? styles.tabActive : {}),
                    }}
                    onClick={() => setSelectedPhase('trash')}
                >
                    Trash ({phasesData?.trash?.indicators?.length ?? 0})
                </button>
            </div>

            <div style={styles.toolbar}>
                {selectedPhase !== 'trash' && (
                    <>
                        <button style={styles.saveButton} onClick={handleSave} disabled={saving || !phasesData}>
                            {saving ? 'Saving…' : 'Save this phase'}
                        </button>
                        <button
                            style={styles.createButton}
                            onClick={() => setCreateModalOpen(true)}
                            disabled={saving}
                        >
                            + Create new indicator
                        </button>
                        <div style={styles.viewToggle}>
                            <button type="button" style={{ ...styles.tab, ...(viewMode === 'tree' ? styles.tabActive : {}) }} onClick={() => setViewMode('tree')}>Tree</button>
                            <button type="button" style={{ ...styles.tab, ...(viewMode === 'flat' ? styles.tabActive : {}) }} onClick={() => setViewMode('flat')}>List</button>
                        </div>
                    </>
                )}
                {saveSuccess && <span style={styles.success}>Saved.</span>}
                {saveConflict && (
                    <span style={styles.conflict}>
                        Someone else saved changes to this phase while you were editing.{' '}
                        <button
                            type="button"
                            style={styles.conflictReload}
                            onClick={load}
                        >
                            Reload latest
                        </button>{' '}
                        (your unsaved changes will be lost)
                    </span>
                )}
                {error && <span style={styles.error}>{error}</span>}
            </div>

            {selectedPhase !== 'trash' && hasTreeIssues && (
                <div style={styles.treeIssuesBar}>
                    {treeIssues.gaps.length > 0 && (
                        <span style={styles.treeIssue}>⚠ Numbering gaps: {treeIssues.gaps.join(', ')}</span>
                    )}
                    {treeIssues.orphans.length > 0 && (
                        <span style={styles.treeIssue}>⚠ Orphans (parent ID missing): {treeIssues.orphans.join(', ')}</span>
                    )}
                    {treeIssues.duplicates.length > 0 && (
                        <span style={styles.treeIssue}>⚠ Duplicate IDs (same id used more than once): {treeIssues.duplicates.join(', ')}</span>
                    )}
                </div>
            )}

            {viewMode === 'tree' && selectedPhase !== 'trash' && indicatorTree.length > 0 && (() => {
                const rootGaps = getGapsForParent(null, treeIssues.gaps);
                const rootCombined: Array<{ type: 'node'; node: TreeIndicatorNode } | { type: 'gap'; id: string }> = [
                    ...indicatorTree.map((n) => ({ type: 'node' as const, node: n })),
                    ...rootGaps.map((id) => ({ type: 'gap' as const, id })),
                ].sort((a, b) => {
                    const segA = a.type === 'node' ? parseInt(a.node.id.split('.').pop() || '0', 10) : parseInt(a.id.split('.').pop() || '0', 10);
                    const segB = b.type === 'node' ? parseInt(b.node.id.split('.').pop() || '0', 10) : parseInt(b.id.split('.').pop() || '0', 10);
                    return segA - segB;
                });
                return (
                    <div style={styles.treeSection}>
                        <h3 style={styles.treeSectionTitle}>Tree structure — edit full details in List view</h3>
                        <div style={styles.treeBlock}>
                            {rootCombined.map((item, rootIndex) => {
                                const isLastRoot = rootIndex === rootCombined.length - 1;
                                if (item.type === 'gap') {
                                    return (
                                        <div key={`gap-${item.id}`} style={styles.treeRow}>
                                            <span style={styles.treeConnectors}>
                                                <span style={styles.treeConnectorBranch}>{isLastRoot ? '└' : '├'}─ </span>
                                            </span>
                                            <span style={styles.treeGapPlaceholder}>— gap: {item.id} —</span>
                                        </div>
                                    );
                                }
                                return (
                                    <TreeNodeRow
                                        key={item.node.id}
                                        node={item.node}
                                        depth={0}
                                        isLast={isLastRoot}
                                        siblingIndex={rootIndex}
                                        gapsForParent={getGapsForParent(null, treeIssues.gaps)}
                                        allGaps={treeIssues.gaps}
                                        orphansSet={new Set(treeIssues.orphans)}
                                        duplicatesSet={new Set(treeIssues.duplicates)}
                                        phase={selectedPhase as Phase}
                                        handleTrash={handleTrash}
                                        onRestructure={(id) => { setRestructure({ phase: selectedPhase as Phase, indicatorId: id, currentId: id }); setRestructureNewId(suggestNextId(indicators, getParentId(id))); }}
                                        onEdit={(id) => setTreeEditModal({ phase: selectedPhase as Phase, indicatorId: id })}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            {(viewMode === 'flat' || selectedPhase === 'trash') && (
            <div style={styles.list}>
                {indicatorsWithExamples.map((ind, index) => (
                    <IndicatorCard
                        key={ind.id}
                        indicator={ind}
                        index={index}
                        phase={selectedPhase}
                        isTrash={selectedPhase === 'trash'}
                        isOrphan={selectedPhase !== 'trash' && treeIssues.orphans.includes(ind.id)}
                        onUpdate={(field, value) => updateIndicator(selectedPhase, index, field, value)}
                        onUpdateExample={(exIndex, field, value) =>
                            updateExample(selectedPhase, ind.id, exIndex, field, value)
                        }
                        onAddExample={() => addExample(selectedPhase, ind.id)}
                        onRemoveExample={(exIndex) => removeExample(selectedPhase, ind.id, exIndex)}
                        onTrash={selectedPhase !== 'trash' ? () => handleTrash(selectedPhase as Phase, ind.id) : undefined}
                        onRestore={selectedPhase === 'trash' ? () => startRestore(ind) : undefined}
                    />
                ))}
            </div>
            )}

            {trashConfirm && (
                <div style={styles.modalOverlay} onClick={() => setTrashConfirm(null)}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>Trash with children?</h3>
                        <p style={styles.modalHint}>{trashConfirm.indicatorId} has {trashConfirm.childCount} descendant(s). Trash all?</p>
                        <div style={styles.modalActions}>
                            <button style={styles.cancelButton} onClick={() => setTrashConfirm(null)}>Cancel</button>
                            <button style={styles.trashButton} onClick={() => handleTrash(trashConfirm.phase, trashConfirm.indicatorId, true)}>Trash all</button>
                        </div>
                    </div>
                </div>
            )}

            {restoreConflict && (
                <div style={styles.modalOverlay} onClick={() => { setRestoreConflict(null); setRestoreNewId(''); }}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>ID already in use</h3>
                        <p style={styles.modalHint}>Restore as different ID:</p>
                        <input style={styles.input} value={restoreNewId} onChange={(e) => setRestoreNewId(e.target.value)} placeholder="e.g. 4.2.10" />
                        <div style={styles.modalActions}>
                            <button style={styles.cancelButton} onClick={() => { setRestoreConflict(null); setRestoreNewId(''); }}>Cancel</button>
                            <button style={styles.saveButton} onClick={() => restoreNewId.trim() && handleRestore(restoreConflict.indicatorId, restoreNewId.trim(), restoreConflict.originalPhase)} disabled={!restoreNewId.trim() || saving}>Restore as</button>
                        </div>
                    </div>
                </div>
            )}

            {treeEditModal && phasesData?.[treeEditModal.phase] && (() => {
                const phaseData = phasesData[treeEditModal.phase];
                const index = phaseData.indicators.findIndex((i) => i.id === treeEditModal.indicatorId);
                const indicator = index >= 0 ? phaseData.indicators[index] : null;
                const examples = phaseData.examples.filter((e) => e.indicatorId === treeEditModal.indicatorId);
                if (!indicator) return null;
                return (
                    <TreeEditModal
                        phase={treeEditModal.phase}
                        indicatorId={treeEditModal.indicatorId}
                        indicator={indicator}
                        examples={examples}
                        index={index}
                        onUpdate={(field, value) => updateIndicator(treeEditModal.phase, index, field, value)}
                        onUpdateExample={(exIndex, field, value) => updateExample(treeEditModal.phase, treeEditModal.indicatorId, exIndex, field, value)}
                        onAddExample={() => addExample(treeEditModal.phase, treeEditModal.indicatorId)}
                        onRemoveExample={(exIndex) => removeExample(treeEditModal.phase, treeEditModal.indicatorId, exIndex)}
                        onClose={() => setTreeEditModal(null)}
                    />
                );
            })()}

            {createModalOpen && selectedPhase !== 'trash' && (
                <CreateNewModal
                    phase={selectedPhase as Phase}
                    existingIds={(phasesData?.[selectedPhase]?.indicators ?? []).map((i) => i.id)}
                    suggestedId={indicators.length ? suggestNextId(indicators, null) : '1'}
                    onClose={() => setCreateModalOpen(false)}
                    onCreate={handleCreate}
                    saving={saving}
                />
            )}

            {restructure && (
                <div style={styles.modalOverlay} onClick={() => { setRestructure(null); setRestructureNewId(''); }}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>Restructure indicator</h3>
                        <p style={styles.modalHint}>Change ID to move or renumber. Edit in List view then Save.</p>
                        <label style={styles.fieldLabel}>Current ID</label>
                        <p style={{ margin: `0 0 ${spacing[4]} 0`, fontFamily: 'monospace', fontWeight: 600 }}>{restructure.currentId}</p>
                        <label style={styles.fieldLabel}>New ID</label>
                        <input style={styles.input} value={restructureNewId} onChange={(e) => setRestructureNewId(e.target.value)} placeholder="e.g. 4.3.1" />
                        <div style={styles.modalActions}>
                            <button style={styles.cancelButton} onClick={() => { setRestructure(null); setRestructureNewId(''); }}>Cancel</button>
                            <button style={styles.saveButton} onClick={() => restructureNewId.trim() && applyRestructure(restructure.phase, restructure.indicatorId, restructureNewId.trim())} disabled={!restructureNewId.trim()}>Apply</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IndicatorVisualizer;

function CreateNewModal({
    phase,
    existingIds,
    suggestedId = '',
    onClose,
    onCreate,
    saving,
}: {
    phase: Phase;
    existingIds: string[];
    suggestedId?: string;
    onClose: () => void;
    onCreate: (data: { id: string; question: string; recommendation: string; detailedQuestion: string; enabling?: boolean; examples?: Array<{ indicator_id: string; example_area: string; example: string }> }) => void;
    saving: boolean;
}) {
    const [id, setId] = useState(suggestedId);
    const [question, setQuestion] = useState('');
    const [recommendation, setRecommendation] = useState('');
    const [detailedQuestion, setDetailedQuestion] = useState('');
    const [enabling, setEnabling] = useState(false);
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!id.trim()) return;
        onCreate({ id: id.trim(), question, recommendation, detailedQuestion, enabling });
    };
    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h3 style={styles.modalTitle}>Create new indicator</h3>
                <p style={styles.modalHint}>
                    ID determines tree placement (e.g. 4.2.10 = child of 4.2). Use a unique id.
                    {suggestedId && <> Suggested: <strong>{suggestedId}</strong></>}
                </p>
                <form onSubmit={handleSubmit}>
                    <label style={styles.fieldLabel}>ID (tree placement)</label>
                    <input
                        style={styles.input}
                        value={id}
                        onChange={(e) => setId(e.target.value)}
                        placeholder="e.g. 4.2.10 or 5.5"
                        required
                    />
                    {existingIds.includes(id.trim()) && id.trim() && (
                        <p style={styles.warning}>This ID already exists</p>
                    )}
                    <label style={styles.fieldLabel}>Question</label>
                    <textarea style={styles.textarea} value={question} onChange={(e) => setQuestion(e.target.value)} rows={2} />
                    <label style={styles.fieldLabel}>Best practice</label>
                    <textarea style={styles.textarea} value={recommendation} onChange={(e) => setRecommendation(e.target.value)} rows={2} />
                    <label style={styles.fieldLabel}>The Why (importance statement)</label>
                    <textarea style={styles.textarea} value={detailedQuestion} onChange={(e) => setDetailedQuestion(e.target.value)} rows={2} />
                    <label style={styles.enablingLabel}>
                        <input type="checkbox" checked={enabling} onChange={(e) => setEnabling(e.target.checked)} />
                        Enabling question
                    </label>
                    <div style={styles.modalActions}>
                        <button type="button" style={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" style={styles.saveButton} disabled={saving || existingIds.includes(id.trim())}>
                            {saving ? 'Creating…' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function TreeEditModal({
    phase,
    indicatorId,
    indicator,
    examples,
    index,
    onUpdate,
    onUpdateExample,
    onAddExample,
    onRemoveExample,
    onClose,
}: {
    phase: Phase;
    indicatorId: string;
    indicator: AdminIndicatorRow;
    examples: AdminExampleRow[];
    index: number;
    onUpdate: (field: keyof AdminIndicatorRow, value: string | boolean) => void;
    onUpdateExample: (exIndex: number, field: 'exampleArea' | 'example', value: string) => void;
    onAddExample: () => void;
    onRemoveExample: (exIndex: number) => void;
    onClose: () => void;
}) {
    const [examplesOpen, setExamplesOpen] = useState(true);
    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h3 style={styles.modalTitle}>Edit indicator {indicatorId}</h3>
                <p style={styles.modalHint}>ID cannot be changed here — use Restructure to change tree position.</p>
                <label style={styles.fieldLabel}>Question</label>
                <textarea style={styles.textarea} value={indicator.question} onChange={(e) => onUpdate('question', e.target.value)} rows={2} />
                <label style={styles.fieldLabel}>Best practice</label>
                <textarea style={styles.textarea} value={indicator.recommendation} onChange={(e) => onUpdate('recommendation', e.target.value)} rows={2} />
                <label style={styles.fieldLabel}>The Why (importance statement)</label>
                <textarea style={styles.textarea} value={indicator.detailedQuestion} onChange={(e) => onUpdate('detailedQuestion', e.target.value)} rows={3} />
                <label style={styles.enablingLabel}>
                    <input type="checkbox" checked={indicator.enabling} onChange={(e) => onUpdate('enabling', e.target.checked)} />
                    Enabling question
                </label>
                <div style={styles.examplesSection}>
                    <button type="button" style={styles.examplesToggle} onClick={() => setExamplesOpen(!examplesOpen)}>{examplesOpen ? '▼' : '▶'} Examples ({examples.length})</button>
                    {examplesOpen && (
                        <div style={styles.examplesList}>
                            {examples.map((ex, exIndex) => (
                                <div key={exIndex} style={styles.exampleRow}>
                                    <input style={styles.exampleArea} placeholder="Area" value={ex.exampleArea} onChange={(e) => onUpdateExample(exIndex, 'exampleArea', e.target.value)} />
                                    <textarea style={styles.exampleText} placeholder="Example text" value={ex.example} onChange={(e) => onUpdateExample(exIndex, 'example', e.target.value)} rows={2} />
                                    <button type="button" style={styles.removeExample} onClick={() => onRemoveExample(exIndex)}>✕</button>
                                </div>
                            ))}
                            <button type="button" style={styles.addExample} onClick={onAddExample}>+ Add example</button>
                        </div>
                    )}
                </div>
                <div style={styles.modalActions}>
                    <button type="button" style={styles.cancelButton} onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}


function TreeNodeRow({
    node,
    depth,
    isLast,
    siblingIndex,
    gapsForParent,
    allGaps,
    orphansSet,
    duplicatesSet,
    phase,
    handleTrash,
    onRestructure,
    onEdit,
}: {
    node: TreeIndicatorNode;
    depth: number;
    isLast: boolean;
    siblingIndex: number;
    gapsForParent: string[];
    allGaps: string[];
    orphansSet: Set<string>;
    duplicatesSet: Set<string>;
    phase: Phase;
    handleTrash: (phase: Phase, indicatorId: string, withChildren?: boolean) => void;
    onRestructure: (id: string) => void;
    onEdit: (id: string) => void;
}) {
    const ind = node.indicator;
    const isOrphan = orphansSet.has(node.id);
    const isDup = duplicatesSet.has(node.id);
    const childGaps = getGapsForParent(node.id, allGaps);
    const combined: Array<{ type: 'node'; node: TreeIndicatorNode } | { type: 'gap'; id: string }> = [
        ...node.children.map((c) => ({ type: 'node' as const, node: c })),
        ...childGaps.map((id) => ({ type: 'gap' as const, id })),
    ].sort((a, b) => {
        const segA = a.type === 'node' ? parseInt(a.node.id.split('.').pop() || '0', 10) : parseInt(a.id.split('.').pop() || '0', 10);
        const segB = b.type === 'node' ? parseInt(b.node.id.split('.').pop() || '0', 10) : parseInt(b.id.split('.').pop() || '0', 10);
        return segA - segB;
    });
    const hasChildrenOrGaps = combined.length > 0;

    return (
        <div style={styles.treeRowWrap}>
            <div style={styles.treeRow}>
                <span style={styles.treeConnectors}>
                    {depth > 0 && Array.from({ length: depth }, (_, i) => (
                        <span key={i} style={styles.treeConnectorPipe}>│</span>
                    ))}
                    <span style={styles.treeConnectorBranch}>{(depth > 0 ? (isLast && !hasChildrenOrGaps ? '└' : '├') : (isLast && !hasChildrenOrGaps ? '└' : '├'))}─ </span>
                </span>
                <span style={styles.treeId}>{node.id}</span>
                {(isOrphan || isDup) && (
                    <>
                        {isOrphan && <span style={styles.badgeOrphan}>orphan</span>}
                        {isDup && <span style={styles.badgeDup}>dup</span>}
                    </>
                )}
                <span style={styles.treeQuestion}>{ind.question}</span>
                <span style={styles.treeActions}>
                    <button type="button" style={styles.treeActionBtn} onClick={() => onEdit(node.id)} title="Edit fields">Edit</button>
                    <button type="button" style={styles.treeActionBtn} onClick={() => onRestructure(node.id)} title="Change ID (restructure)">Restructure</button>
                    <button type="button" style={styles.treeTrashBtn} onClick={() => handleTrash(phase, node.id)} title="Trash">Trash</button>
                </span>
            </div>
            {combined.map((item, idx) => {
                const isLastItem = idx === combined.length - 1;
                if (item.type === 'gap') {
                    return (
                        <div key={`gap-${item.id}`} style={styles.treeRow}>
                            <span style={styles.treeConnectors}>
                                {Array.from({ length: depth + 1 }, (_, i) => (
                                    <span key={i} style={styles.treeConnectorPipe}>│</span>
                                ))}
                                <span style={styles.treeConnectorBranch}>{isLastItem ? '└' : '├'}─ </span>
                            </span>
                            <span style={styles.treeGapPlaceholder}>— gap: {item.id} —</span>
                        </div>
                    );
                }
                return (
                    <TreeNodeRow
                        key={item.node.id}
                        node={item.node}
                        depth={depth + 1}
                        isLast={isLastItem}
                        siblingIndex={idx}
                        gapsForParent={getGapsForParent(item.node.id, allGaps)}
                        allGaps={allGaps}
                        orphansSet={orphansSet}
                        duplicatesSet={duplicatesSet}
                        phase={phase}
                        handleTrash={handleTrash}
                        onRestructure={onRestructure}
                        onEdit={onEdit}
                    />
                );
            })}
        </div>
    );
}

function IndicatorCard({
    indicator,
    index,
    phase,
    isTrash,
    isOrphan,
    onUpdate,
    onUpdateExample,
    onAddExample,
    onRemoveExample,
    onTrash,
    onRestore,
}: {
    indicator: IndicatorWithExamples & { original_phase?: string };
    index: number;
    phase: PhaseOrTrash;
    isTrash: boolean;
    isOrphan?: boolean;
    onUpdate: (field: keyof AdminIndicatorRow, value: string | boolean) => void;
    onUpdateExample: (exampleIndex: number, field: 'exampleArea' | 'example', value: string) => void;
    onAddExample: () => void;
    onRemoveExample: (exampleIndex: number) => void;
    onTrash?: () => void;
    onRestore?: () => void;
}) {
    const [examplesOpen, setExamplesOpen] = useState(false);

    return (
        <section style={styles.card}>
            <div style={styles.cardHeader}>
                <span style={styles.id}>{indicator.id}</span>
                {isOrphan && <span style={styles.badgeOrphan}>orphan</span>}
                {isTrash && indicator.original_phase && (
                    <span style={styles.originalPhase}>from {PHASE_LABELS[indicator.original_phase as Phase] || indicator.original_phase}</span>
                )}
                {!isTrash && (
                    <label style={styles.enablingLabel}>
                        <input
                            type="checkbox"
                            checked={indicator.enabling}
                            onChange={(e) => onUpdate('enabling', e.target.checked)}
                        />
                        Enabling question
                    </label>
                )}
                {onTrash && (
                    <button type="button" style={styles.trashButton} onClick={onTrash} title="Trash indicator and all examples">
                        Trash
                    </button>
                )}
                {onRestore && (
                    <button type="button" style={styles.restoreButton} onClick={onRestore} title="Restore to original phase">
                        Restore
                    </button>
                )}
            </div>

            <label style={styles.fieldLabel}>Question</label>
            <textarea
                style={styles.textarea}
                value={indicator.question}
                onChange={(e) => onUpdate('question', e.target.value)}
                rows={2}
                readOnly={isTrash}
            />

            <label style={styles.fieldLabel}>Best practice</label>
            <textarea
                style={styles.textarea}
                value={indicator.recommendation}
                onChange={(e) => onUpdate('recommendation', e.target.value)}
                rows={2}
                readOnly={isTrash}
            />

            <label style={styles.fieldLabel}>The Why (importance statement)</label>
            <textarea
                style={styles.textarea}
                value={indicator.detailedQuestion}
                onChange={(e) => onUpdate('detailedQuestion', e.target.value)}
                rows={3}
                readOnly={isTrash}
            />

            {!isTrash && (
            <div style={styles.examplesSection}>
                <button
                    type="button"
                    style={styles.examplesToggle}
                    onClick={() => setExamplesOpen(!examplesOpen)}
                >
                    {examplesOpen ? '▼' : '▶'} Examples ({indicator.examples.length})
                </button>
                {examplesOpen && (
                    <div style={styles.examplesList}>
                        {indicator.examples.map((ex, exIndex) => (
                            <div key={exIndex} style={styles.exampleRow}>
                                <input
                                    style={styles.exampleArea}
                                    placeholder="Area"
                                    value={ex.exampleArea}
                                    onChange={(e) => onUpdateExample(exIndex, 'exampleArea', e.target.value)}
                                />
                                <textarea
                                    style={styles.exampleText}
                                    placeholder="Example text"
                                    value={ex.example}
                                    onChange={(e) => onUpdateExample(exIndex, 'example', e.target.value)}
                                    rows={2}
                                />
                                <button
                                    type="button"
                                    style={styles.removeExample}
                                    onClick={() => onRemoveExample(exIndex)}
                                    title="Remove example"
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                        <button type="button" style={styles.addExample} onClick={onAddExample}>
                            + Add example
                        </button>
                    </div>
                )}
            </div>
            )}
        </section>
    );
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        minHeight: '100vh',
        backgroundColor: colors.bgLight,
        padding: spacing[6],
        paddingBottom: spacing[16],
    },
    loading: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing[4],
        backgroundColor: colors.bgLight,
        fontSize: fonts.lg,
        color: colors.textSecondary,
    },
    header: {
        marginBottom: spacing[6],
    },
    headerRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing[4],
    },
    backButton: {
        ...buttonBase,
        flexShrink: 0,
        backgroundColor: colors.bgWarm,
        color: colors.textSecondary,
        border: `1px solid ${colors.border}`,
    },
    title: {
        fontSize: fonts['2xl'],
        fontWeight: 700,
        color: colors.text,
        margin: 0,
        marginBottom: spacing[2],
    },
    subtitle: {
        fontSize: fonts.sm,
        color: colors.textSecondary,
        margin: 0,
    },
    tabs: {
        display: 'flex',
        gap: spacing[2],
        marginBottom: spacing[6],
        flexWrap: 'wrap',
    },
    tab: {
        ...buttonBase,
        backgroundColor: colors.bgWarm,
        color: colors.textSecondary,
        border: `1px solid ${colors.border}`,
    },
    tabActive: {
        backgroundColor: colors.primary,
        color: colors.white,
        borderColor: colors.primary,
    },
    tabTrash: {
        marginLeft: 'auto',
    },
    viewToggle: { display: 'flex', gap: spacing[1], marginLeft: spacing[4] },
    treeIssuesBar: { display: 'flex', flexDirection: 'column', gap: spacing[2], marginBottom: spacing[4], padding: spacing[3], backgroundColor: colors.bgWarm, borderRadius: radii.md, border: `1px solid ${colors.border}` },
    treeIssue: { fontSize: fonts.sm, color: colors.no, fontWeight: 600 },
    treeSection: { marginBottom: spacing[8] },
    treeSectionTitle: { fontSize: fonts.lg, fontWeight: 600, color: colors.text, margin: `0 0 ${spacing[4]} 0` },
    treeBlock: { display: 'flex', flexDirection: 'column', gap: spacing[4] },
    treeBadges: { display: 'flex', gap: spacing[2], marginBottom: spacing[1] },
    badgeGap: { fontSize: fonts.xs, padding: `2px ${spacing[2]}`, backgroundColor: '#fef3c7', color: '#92400e', borderRadius: radii.sm, fontWeight: 600 },
    badgeOrphan: { fontSize: fonts.xs, padding: `2px ${spacing[2]}`, backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: radii.sm, fontWeight: 600 },
    badgeDup: { fontSize: fonts.xs, padding: `2px ${spacing[2]}`, backgroundColor: '#fce7f3', color: '#9d174d', borderRadius: radii.sm, fontWeight: 600 },
    adminAuthBox: {
        backgroundColor: colors.white,
        borderRadius: radii.lg,
        padding: spacing[6],
        boxShadow: shadows.md,
        maxWidth: 420,
        width: '100%',
        border: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: spacing[3],
    },
    treeRowWrap: { marginBottom: 0 },
    treeRow: { display: 'flex', alignItems: 'flex-start', gap: spacing[2], padding: `${spacing[2]} ${spacing[3]}`, borderBottom: `1px solid ${colors.borderLight}`, fontSize: fonts.sm, flexWrap: 'wrap' },
    treeConnectors: { display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0, color: colors.textMuted, fontFamily: 'monospace' },
    treeConnectorPipe: { marginRight: 2 },
    treeConnectorBranch: { marginRight: spacing[1] },
    treeId: { flexShrink: 0, fontWeight: 600, fontFamily: 'monospace', color: colors.text, minWidth: '3em' },
    treeQuestion: { flex: '1 1 280px', color: colors.text, lineHeight: 1.35 },
    treeActions: { display: 'flex', gap: spacing[2], flexShrink: 0 },
    treeActionBtn: { ...buttonBase, padding: `${spacing[1]} ${spacing[2]}`, fontSize: fonts.xs, backgroundColor: colors.bgWarm, color: colors.textSecondary, minHeight: 'auto' },
    treeTrashBtn: { ...buttonBase, padding: `${spacing[1]} ${spacing[2]}`, fontSize: fonts.xs, backgroundColor: colors.no, color: 'white', minHeight: 'auto' },
    treeGapPlaceholder: { fontStyle: 'italic', color: colors.textMuted, fontSize: fonts.xs },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[4],
        marginBottom: spacing[6],
    },
    saveButton: {
        ...buttonBase,
        backgroundColor: colors.yes,
        color: 'white',
    },
    createButton: {
        ...buttonBase,
        backgroundColor: colors.primary,
        color: 'white',
    },
    success: {
        color: colors.yes,
        fontSize: fonts.sm,
    },
    conflict: {
        color: '#b45309',
        fontSize: fonts.sm,
        display: 'inline-flex',
        alignItems: 'center',
        gap: spacing[2],
    },
    conflictReload: {
        background: 'none',
        border: 'none',
        padding: 0,
        color: '#b45309',
        fontWeight: 700,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontSize: 'inherit',
    },
    error: {
        color: colors.no,
        fontSize: fonts.sm,
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[6],
        maxWidth: '1000px',
    },
    card: {
        backgroundColor: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: radii.lg,
        padding: spacing[6],
        boxShadow: shadows.sm,
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[4],
        marginBottom: spacing[4],
        flexWrap: 'wrap',
    },
    id: {
        fontWeight: 600,
        color: colors.text,
        fontFamily: 'monospace',
        fontSize: fonts.base,
    },
    originalPhase: {
        fontSize: fonts.xs,
        color: colors.textMuted,
        fontStyle: 'italic',
    },
    trashButton: {
        ...buttonBase,
        marginLeft: 'auto',
        backgroundColor: colors.no,
        color: 'white',
        padding: `${spacing[1]} ${spacing[3]}`,
        fontSize: fonts.xs,
        minHeight: 'auto',
    },
    restoreButton: {
        ...buttonBase,
        marginLeft: spacing[2],
        backgroundColor: colors.yes,
        color: 'white',
        padding: `${spacing[1]} ${spacing[3]}`,
        fontSize: fonts.xs,
        minHeight: 'auto',
    },
    enablingLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[2],
        fontSize: fonts.sm,
        color: colors.textSecondary,
        cursor: 'pointer',
    },
    fieldLabel: {
        display: 'block',
        fontSize: fonts.xs,
        fontWeight: 600,
        color: colors.textSecondary,
        marginBottom: spacing[1],
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    textarea: {
        width: '100%',
        boxSizing: 'border-box',
        padding: spacing[3],
        border: `1px solid ${colors.border}`,
        borderRadius: radii.sm,
        fontSize: fonts.sm,
        fontFamily: fonts.family,
        marginBottom: spacing[4],
        resize: 'vertical',
        minHeight: '4em',
    },
    examplesSection: {
        marginTop: spacing[4],
        borderTop: `1px solid ${colors.borderLight}`,
        paddingTop: spacing[4],
    },
    examplesToggle: {
        ...buttonBase,
        backgroundColor: 'transparent',
        color: colors.primary,
        fontSize: fonts.sm,
        padding: spacing[2],
        minHeight: 'auto',
    },
    examplesList: {
        marginTop: spacing[2],
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
    },
    exampleRow: {
        display: 'grid',
        gridTemplateColumns: '120px 1fr auto',
        gap: spacing[2],
        alignItems: 'start',
    },
    exampleArea: {
        padding: spacing[2],
        border: `1px solid ${colors.border}`,
        borderRadius: radii.sm,
        fontSize: fonts.sm,
    },
    exampleText: {
        padding: spacing[2],
        border: `1px solid ${colors.border}`,
        borderRadius: radii.sm,
        fontSize: fonts.sm,
        fontFamily: fonts.family,
        resize: 'vertical',
        minHeight: '3em',
    },
    removeExample: {
        ...buttonBase,
        backgroundColor: 'transparent',
        color: colors.textMuted,
        padding: spacing[2],
        minHeight: 'auto',
        fontSize: fonts.lg,
    },
    addExample: {
        ...buttonBase,
        backgroundColor: colors.bgWarm,
        color: colors.textSecondary,
        border: `1px dashed ${colors.border}`,
        alignSelf: 'flex-start',
    },
    modalOverlay: {
        position: 'fixed' as const,
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: colors.white,
        borderRadius: radii.lg,
        padding: spacing[6],
        maxWidth: 520,
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
    },
    modalTitle: {
        margin: `0 0 ${spacing[8]} 0`,
        fontSize: fonts.xl,
        color: colors.text,
    },
    modalHint: {
        margin: `0 0 ${spacing[4]} 0`,
        fontSize: fonts.sm,
        color: colors.textSecondary,
    },
    input: {
        width: '100%',
        boxSizing: 'border-box',
        padding: spacing[3],
        border: `1px solid ${colors.border}`,
        borderRadius: radii.sm,
        fontSize: fonts.sm,
        marginBottom: spacing[4],
    },
    warning: {
        color: colors.no,
        fontSize: fonts.sm,
        margin: `0 0 ${spacing[4]} 0`,
    },
    modalActions: {
        display: 'flex',
        gap: spacing[4],
        marginTop: spacing[6],
    },
    cancelButton: {
        ...buttonBase,
        backgroundColor: colors.bgWarm,
        color: colors.textSecondary,
    },
};
